import {
  getGranthaReferenceKey,
  getNestedKey,
  getNestedTextId,
  parseLineIdFromNestedTextId,
  stableNestedLineId,
} from './bidhiNestedEditKeys';
import type { LineOverrideLike } from './bookStyleCss';
import type { BookDefaultStylesV1 } from './bookDefaults';
import { mergeGlobalTagStyle } from './tagLineStyles';

export type TagBlockTarget = { id: number; tag: string };
export type TagNestedTarget = { key: string; content: string };
export type TagGranthaTarget = { key: string };

export type TaggedStyleTargets = {
  blockIds: number[];
  nested: TagNestedTarget[];
  grantha: TagGranthaTarget[];
};

/** Normalize line metadata `tag` to a string array (string, string[], or key-value entry). */
export function normalizeMetadataTagValue(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
      .filter(Boolean);
  }
  return [];
}

/** Line metadata tags: `{ key: 'tag', value }`, `metadata.tag` string, or `metadata.tag` string[]. */
export function extractMetadataTagNames(metadata: unknown): string[] {
  if (!metadata) return [];

  const names: string[] = [];
  const addUnique = (tag: string) => {
    if (tag && !names.includes(tag)) names.push(tag);
  };

  if (Array.isArray(metadata)) {
    const found = metadata.find(
      (m) =>
        m &&
        typeof m === 'object' &&
        String((m as { key?: string }).key ?? '').toLowerCase() === 'tag' &&
        ((typeof (m as { value?: unknown }).value === 'string' &&
          Boolean((m as { value?: string }).value?.trim?.())) ||
          (Array.isArray((m as { value?: unknown }).value) &&
            (m as { value?: unknown[] }).value?.some(
              (v) => typeof v === 'string' && v.trim(),
            ))),
    );
    const raw = (found as { value?: unknown } | undefined)?.value;
    if (typeof raw === 'string') {
      const v = raw.trim();
      if (v) addUnique(v);
    } else if (Array.isArray(raw)) {
      raw.forEach((x) => {
        if (typeof x === 'string' && x.trim()) addUnique(x.trim());
      });
    }
    return names;
  }

  if (typeof metadata === 'object') {
    const record = metadata as Record<string, unknown>;
    for (const [key, value] of Object.entries(record)) {
      if (key.toLowerCase() !== 'tag') continue;
      if (typeof value === 'string' && value.trim()) {
        addUnique(value.trim());
      } else if (Array.isArray(value)) {
        value.forEach((x) => {
          if (typeof x === 'string' && x.trim()) addUnique(x.trim());
        });
      }
    }
    return names;
  }

  return names;
}

/** First line metadata tag, if any. */
export function extractMetadataTagName(metadata: unknown): string | undefined {
  return extractMetadataTagNames(metadata)[0];
}

export function metadataHasTagName(metadata: unknown, tagName: string): boolean {
  if (!tagName) return false;
  return extractMetadataTagNames(metadata).includes(tagName);
}

type BidhiLineTagSource = {
  lines?: Array<{ metadata?: unknown }>;
  children?: BidhiLineTagSource[];
};

/** All line metadata tags used across loaded bidhis (recursive through children). */
export function collectMetadataTagsFromBidhis(
  bidhis: BidhiLineTagSource[] | null | undefined,
): string[] {
  const set = new Set<string>();

  const walk = (nodes: BidhiLineTagSource[] | undefined) => {
    if (!nodes?.length) return;
    for (const node of nodes) {
      for (const line of node.lines || []) {
        for (const tag of extractMetadataTagNames(line.metadata)) {
          set.add(tag);
        }
      }
      walk(node.children);
    }
  };

  walk(bidhis ?? []);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Entity tags from API: `bidhi.tags`, `sankalpa.tags`, `puja.tags`, etc. */
export function extractEntityTagNames(tags: unknown): string[] {
  if (!Array.isArray(tags)) return [];
  return tags
    .map((t) => (t && typeof t === 'object' && typeof (t as { name?: string }).name === 'string'
      ? (t as { name: string }).name.trim()
      : ''))
    .filter(Boolean);
}

export function entityHasTag(entity: unknown, tagName: string): boolean {
  if (!tagName) return false;
  return extractEntityTagNames((entity as { tags?: unknown })?.tags).includes(tagName);
}

/** Shloka line tag from grantha reference payload: `shlokaLines[].tag` */
export function extractShlokaLineTag(shlokaLine: unknown): string | undefined {
  if (!shlokaLine || typeof shlokaLine !== 'object') return undefined;
  const v = (shlokaLine as { tag?: string }).tag;
  if (typeof v === 'string' && v.trim()) return v.trim();
  return undefined;
}

export function addShlokaTagsFromGranthaReferences(set: Set<string>, granthaReferences: unknown): void {
  if (!Array.isArray(granthaReferences)) return;
  for (const gRef of granthaReferences) {
    for (const ref of (gRef as { references?: unknown[] })?.references || []) {
      for (const sl of (ref as { shlokaLines?: unknown[] })?.shlokaLines || []) {
        const tag = extractShlokaLineTag(sl);
        if (tag) set.add(tag);
      }
    }
  }
}

export function granthaReferenceHasShlokaTag(granthaRef: unknown, tagName: string): boolean {
  if (!tagName || !granthaRef || typeof granthaRef !== 'object') return false;
  for (const ref of (granthaRef as { references?: unknown[] }).references || []) {
    for (const sl of (ref as { shlokaLines?: unknown[] }).shlokaLines || []) {
      if (extractShlokaLineTag(sl) === tagName) return true;
    }
  }
  return false;
}

function walkBidhiGranthaReferences(
  bidhiData: any,
  onGranthaRef: (gRef: any) => void,
): void {
  if (!bidhiData) return;
  for (const gRef of bidhiData.granthaReferences || []) {
    onGranthaRef(gRef);
  }
  (bidhiData.children || []).forEach((child: any) => walkBidhiGranthaReferences(child, onGranthaRef));
}

export function addTagNamesToSet(set: Set<string>, ...sources: unknown[]): void {
  for (const source of sources) {
    if (typeof source === 'string' && source.trim()) {
      set.add(source.trim());
      continue;
    }
    for (const meta of extractMetadataTagNames(source)) {
      set.add(meta);
    }
    for (const name of extractEntityTagNames(source)) {
      set.add(name);
    }
  }
}

type WalkBidhiOptions = {
  bidhiData: any;
  nestedAddedLines: Record<string, any[]>;
  onBidhi?: (bidhiData: any) => void;
  onBidhiLine?: (line: any, bidhiData: any, idx: number, bidhiTagged: boolean) => void;
  onSankalpa?: (sankalpaData: any, bidhiData: any) => void;
  onSankalpaLine?: (line: any, sankalpaData: any, idx: number, sankalpaTagged: boolean) => void;
  inheritEntityTag?: boolean;
  selectedTagName?: string;
};

function walkLineAttachedBidhis(line: any, options: WalkBidhiOptions, inheritedTag: boolean): void {
  const seenBidhiIds = new Set<number>();

  const walkLinked = (linkedBidhi: any) => {
    if (!linkedBidhi) return;
    const linkedId = Number(linkedBidhi.id);
    if (Number.isFinite(linkedId) && seenBidhiIds.has(linkedId)) return;
    if (Number.isFinite(linkedId)) seenBidhiIds.add(linkedId);
    walkBidhiTree({
      ...options,
      bidhiData: linkedBidhi,
      inheritEntityTag: inheritedTag,
    });
  };

  (line.multipleBidhiLinks || []).forEach((link: any) => walkLinked(link?.bidhi));
  (line.embeddedBidhis || []).forEach((embedded: any) => walkLinked(embedded));
}

function walkBidhiTree({
  bidhiData,
  nestedAddedLines,
  onBidhi,
  onBidhiLine,
  onSankalpa,
  onSankalpaLine,
  inheritEntityTag = false,
  selectedTagName = '',
}: WalkBidhiOptions): void {
  if (!bidhiData) return;

  const bidhiTagged =
    inheritEntityTag || (selectedTagName ? entityHasTag(bidhiData, selectedTagName) : false);
  onBidhi?.(bidhiData);

  const bidhiKey = getNestedKey('bidhi', bidhiData.id ?? bidhiData.name);
  const baseLines = [
    ...(bidhiData.lines || []),
    ...(nestedAddedLines[bidhiKey] || []),
  ].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

  baseLines.forEach((line: any, idx: number) => {
    onBidhiLine?.(line, bidhiData, idx, bidhiTagged);
    (line.sankalpaLinks || []).forEach((link: any) => {
      const sankalpaData = link?.sankalpa;
      if (!sankalpaData) return;
      onSankalpa?.(sankalpaData, bidhiData);

      const sankalpaTagged =
        bidhiTagged || (selectedTagName ? entityHasTag(sankalpaData, selectedTagName) : false);
      const sankalpaKey = getNestedKey('sankalpa', sankalpaData.id ?? sankalpaData.name);
      const sankalpaLines = [
        ...(sankalpaData.lines || []),
        ...(nestedAddedLines[sankalpaKey] || []),
      ].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

      sankalpaLines.forEach((sLine: any, sIdx: number) =>
        onSankalpaLine?.(sLine, sankalpaData, sIdx, sankalpaTagged),
      );
    });

    walkLineAttachedBidhis(
      line,
      {
        bidhiData,
        nestedAddedLines,
        onBidhi,
        onBidhiLine,
        onSankalpa,
        onSankalpaLine,
        selectedTagName,
      },
      bidhiTagged,
    );
  });

  (bidhiData.children || []).forEach((child: any) =>
    walkBidhiTree({
      bidhiData: child,
      nestedAddedLines,
      onBidhi,
      onBidhiLine,
      onSankalpa,
      onSankalpaLine,
      inheritEntityTag: bidhiTagged,
      selectedTagName,
    }),
  );
}

export function collectTagBlockTargets(
  linesToRender: Array<{ id?: number; metadata?: unknown }>,
  entityWithTags?: unknown,
): TagBlockTarget[] {
  const targets: TagBlockTarget[] = [];
  const entityTags = extractEntityTagNames((entityWithTags as { tags?: unknown })?.tags);

  // We treat entity-level tags (e.g. `bidhi.tags`) as applying to the *entity itself*,
  // not to every line inside the entity. To keep the style system consistent (it stores
  // overrides by line id), we map entity tags onto a single representative block id.
  //
  // This prevents "Edit by tag" from unintentionally styling all children lines when a
  // tag exists on the bidhi/sankalpa entity rather than on specific lines.
  const representativeLineId =
    typeof linesToRender?.[0]?.id === 'number' ? (linesToRender[0]!.id as number) : undefined;

  for (const line of linesToRender) {
    const id = line.id;
    if (typeof id !== 'number') continue;

    for (const metaTag of extractMetadataTagNames(line.metadata)) {
      targets.push({ id, tag: metaTag });
    }
  }

  if (representativeLineId != null) {
    for (const tag of entityTags) {
      targets.push({ id: representativeLineId, tag });
    }
  }

  return targets;
}

function collectLineLevelTagsInBidhi(options: {
  linesToRender: Array<{ id?: number; metadata?: unknown }>;
  bidhiRoots: any[];
  nestedAddedLines: Record<string, any[]>;
  puja?: { tags?: unknown; lines?: any[] } | null;
}): Set<string> {
  const { linesToRender, bidhiRoots, nestedAddedLines, puja } = options;
  const set = new Set<string>();

  for (const line of linesToRender) {
    addTagNamesToSet(set, line.metadata);
  }

  const walkRoots = (roots: any[]) => {
    roots.forEach((root) => {
      if (!root) return;
      walkBidhiTree({
        bidhiData: root,
        nestedAddedLines,
        onBidhi: (bidhiData) => {
          addShlokaTagsFromGranthaReferences(set, bidhiData?.granthaReferences);
        },
        onBidhiLine: (line) => addTagNamesToSet(set, line?.metadata),
        onSankalpaLine: (line) => addTagNamesToSet(set, line?.metadata),
      });
    });
  };

  walkRoots(bidhiRoots);

  (puja?.lines || []).forEach((pl: any) => {
    addTagNamesToSet(set, pl?.metadata);
    (pl?.bidhiLinks || []).forEach((bl: any) => {
      if (bl?.bidhi) walkRoots([bl.bidhi]);
    });
    (pl?.sankalpaLinks || []).forEach((sl: any) => {
      const sankalpaData = sl?.sankalpa;
      if (!sankalpaData) return;
      const sankalpaKey = getNestedKey('sankalpa', sankalpaData.id ?? sankalpaData.name);
      const sankalpaLines = [
        ...(sankalpaData.lines || []),
        ...(nestedAddedLines[sankalpaKey] || []),
      ];
      sankalpaLines.forEach((l: any) => addTagNamesToSet(set, l?.metadata));
    });
  });

  return set;
}

export function tagHasStyleTargets(
  tagName: string,
  options: Omit<Parameters<typeof collectTaggedStyleTargets>[0], 'selectedTagName'>,
): boolean {
  if (!tagName) return false;
  const targets = collectTaggedStyleTargets({ ...options, selectedTagName: tagName });
  return (
    targets.blockIds.length > 0 || targets.nested.length > 0 || targets.grantha.length > 0
  );
}

export function collectAvailableLineTags(options: {
  linesToRender: Array<{ id?: number; metadata?: unknown }>;
  bidhiRoots: any[];
  nestedAddedLines: Record<string, any[]>;
  puja?: { tags?: unknown; lines?: any[] } | null;
  entityWithTags?: unknown;
  catalogTagNames?: string[];
  requireStyleTargets?: boolean;
}): string[] {
  const {
    linesToRender,
    bidhiRoots,
    nestedAddedLines,
    puja,
    entityWithTags,
    catalogTagNames,
    requireStyleTargets = false,
  } = options;

  const styleTargetOptions = {
    linesToRender,
    bidhiRoots,
    nestedAddedLines,
    puja,
    entityWithTags,
  };

  const catalog = Array.isArray(catalogTagNames)
    ? new Set(catalogTagNames.map((t) => String(t ?? '').trim()).filter(Boolean))
    : null;

  if (catalog && catalog.size > 0) {
    const present = collectLineLevelTagsInBidhi({ linesToRender, bidhiRoots, nestedAddedLines, puja });
    let out = Array.from(present).filter((tag) => catalog.has(tag));
    if (requireStyleTargets) {
      out = out.filter((tag) => tagHasStyleTargets(tag, styleTargetOptions));
    }
    return out.sort((a, b) => a.localeCompare(b));
  }

  const set = new Set<string>();

  addTagNamesToSet(set, (entityWithTags as { tags?: unknown })?.tags);

  for (const line of linesToRender) {
    addTagNamesToSet(set, line.metadata);
  }

  addTagNamesToSet(set, puja?.tags);

  const walkRoots = (roots: any[]) => {
    roots.forEach((root) => {
      if (!root) return;
      walkBidhiTree({
        bidhiData: root,
        nestedAddedLines,
        onBidhi: (bidhiData) => {
          addTagNamesToSet(set, bidhiData?.tags);
          addShlokaTagsFromGranthaReferences(set, bidhiData?.granthaReferences);
        },
        onBidhiLine: (line) => addTagNamesToSet(set, line?.metadata),
        onSankalpa: (sankalpaData) => addTagNamesToSet(set, sankalpaData?.tags),
        onSankalpaLine: (line) => addTagNamesToSet(set, line?.metadata),
      });
    });
  };

  walkRoots(bidhiRoots);

  (puja?.lines || []).forEach((pl: any) => {
    addTagNamesToSet(set, pl?.metadata);
    (pl?.bidhiLinks || []).forEach((bl: any) => {
      if (bl?.bidhi) walkRoots([bl.bidhi]);
    });
    (pl?.sankalpaLinks || []).forEach((sl: any) => {
      const sankalpaData = sl?.sankalpa;
      if (!sankalpaData) return;
      addTagNamesToSet(set, sankalpaData.tags);
      const sankalpaKey = getNestedKey('sankalpa', sankalpaData.id ?? sankalpaData.name);
      const sankalpaLines = [
        ...(sankalpaData.lines || []),
        ...(nestedAddedLines[sankalpaKey] || []),
      ];
      sankalpaLines.forEach((l: any) => addTagNamesToSet(set, l?.metadata));
    });
  });

  let out = Array.from(set);
  if (requireStyleTargets) {
    out = out.filter((tag) => tagHasStyleTargets(tag, styleTargetOptions));
  }
  return out.sort((a, b) => a.localeCompare(b));
}

export function collectNestedTargetsForTag(options: {
  selectedTagName: string;
  bidhiRoots: any[];
  nestedAddedLines: Record<string, any[]>;
  puja?: { tags?: unknown; lines?: any[] } | null;
}): TagNestedTarget[] {
  const { selectedTagName, bidhiRoots, nestedAddedLines, puja } = options;
  if (!selectedTagName) return [];

  const out: TagNestedTarget[] = [];
  const seenKeys = new Set<string>();
  const pujaTagged = entityHasTag(puja, selectedTagName);

  const pushTarget = (key: string, content: string) => {
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    out.push({ key, content });
  };

  const pushBidhiLine = (bidhiData: any, line: any, idx: number, inherited: boolean) => {
    const bidhiTagged = inherited || entityHasTag(bidhiData, selectedTagName);
    const lineTagged = metadataHasTagName(line?.metadata, selectedTagName);
    if (!pujaTagged && !bidhiTagged && !lineTagged) return;

    const bidhiKey = getNestedKey('bidhi', bidhiData.id ?? bidhiData.name);
    const localLineId = stableNestedLineId(bidhiKey, line, line?.order ?? idx);
    pushTarget(getNestedTextId(bidhiKey, localLineId), String(line?.content ?? ''));
  };

  const pushSankalpaLine = (
    sankalpaData: any,
    line: any,
    idx: number,
    inherited: boolean,
  ) => {
    const sankalpaTagged = inherited || entityHasTag(sankalpaData, selectedTagName);
    const lineTagged = metadataHasTagName(line?.metadata, selectedTagName);
    if (!pujaTagged && !sankalpaTagged && !lineTagged) return;

    const sankalpaKey = getNestedKey('sankalpa', sankalpaData.id ?? sankalpaData.name);
    const localLineId = stableNestedLineId(sankalpaKey, line, line?.order ?? idx);
    pushTarget(getNestedTextId(sankalpaKey, localLineId), String(line?.content ?? ''));
  };

  const walkRoots = (roots: any[], inherited = pujaTagged) => {
    roots.forEach((root) => {
      if (!root) return;
      walkBidhiTree({
        bidhiData: root,
        nestedAddedLines,
        inheritEntityTag: inherited,
        selectedTagName,
        onBidhiLine: (line, bidhiData, idx, bidhiTagged) => {
          pushBidhiLine(bidhiData, line, idx, inherited || bidhiTagged);
        },
        onSankalpaLine: (line, sankalpaData, idx, sankalpaTagged) => {
          pushSankalpaLine(sankalpaData, line, idx, inherited || sankalpaTagged);
        },
      });
    });
  };

  walkRoots(bidhiRoots);

  (puja?.lines || []).forEach((pl: any) => {
    (pl?.bidhiLinks || []).forEach((bl: any) => {
      if (bl?.bidhi) walkRoots([bl.bidhi], pujaTagged);
    });
    (pl?.sankalpaLinks || []).forEach((sl: any) => {
      const sankalpaData = sl?.sankalpa;
      if (!sankalpaData) return;
      const sankalpaKey = getNestedKey('sankalpa', sankalpaData.id ?? sankalpaData.name);
      const baseLines = [
        ...(sankalpaData.lines || []),
        ...(nestedAddedLines[sankalpaKey] || []),
      ].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      baseLines.forEach((line: any, idx: number) =>
        pushSankalpaLine(sankalpaData, line, idx, pujaTagged),
      );
    });
  });

  return out;
}

export function collectTaggedBlockIds(
  tagBlockTargets: TagBlockTarget[],
  selectedTagName: string,
  linesToRender: Array<{ id?: number }>,
  entityWithTags?: unknown,
  bidhiRoots: any[] = [],
): number[] {
  const ids = new Set<number>(
    tagBlockTargets.filter((t) => t.tag === selectedTagName).map((t) => t.id),
  );

  const walkRoots = (roots: any[], inheritedEntityTag = false) => {
    roots.forEach((root) => {
      if (!root) return;
      walkBidhiTree({
        bidhiData: root,
        nestedAddedLines: {},
        inheritEntityTag: inheritedEntityTag,
        selectedTagName,
        onBidhiLine: (line, bidhiData, idx, bidhiTagged) => {
          const lineTagged = extractMetadataTagName(line?.metadata) === selectedTagName;
          if (lineTagged && typeof line.id === 'number') {
            ids.add(line.id);
          }
        },
      });
    });
  };

  walkRoots(bidhiRoots, false);

  return Array.from(ids);
}

export function collectSpanLineIdsForTag(targets: TaggedStyleTargets): number[] {
  const ids = new Set<number>(targets.blockIds);
  for (const nested of targets.nested) {
    const lineId = parseLineIdFromNestedTextId(nested.key);
    if (lineId != null) ids.add(lineId);
  }
  return Array.from(ids);
}

export function getExistingTagStyleFromTargets(
  targets: TaggedStyleTargets,
  overrides: {
    lineStyleOverrides?: Record<number, LineOverrideLike>;
    nestedTextOverrides?: Record<string, LineOverrideLike & { content?: string }>;
    granthaStyleOverrides?: Record<string, LineOverrideLike>;
  },
  bookDefaults?: BookDefaultStylesV1,
  selectedTagName?: string,
): LineOverrideLike {
  let perBook: LineOverrideLike = {};
  const firstBlockId = targets.blockIds[0];
  if (firstBlockId != null) {
    perBook = overrides.lineStyleOverrides?.[firstBlockId] || {};
  } else {
    const firstNestedKey = targets.nested[0]?.key;
    if (firstNestedKey) {
      perBook = overrides.nestedTextOverrides?.[firstNestedKey] || {};
    } else {
      const firstGranthaKey = targets.grantha[0]?.key;
      if (firstGranthaKey) {
        perBook = overrides.granthaStyleOverrides?.[firstGranthaKey] || {};
      }
    }
  }
  return mergeGlobalTagStyle(bookDefaults, selectedTagName, perBook) || {};
}

export function collectGranthaTargetsForTag(options: {
  selectedTagName: string;
  bidhiRoots: any[];
  puja?: { tags?: unknown; lines?: any[] } | null;
}): TagGranthaTarget[] {
  const { selectedTagName, bidhiRoots, puja } = options;
  if (!selectedTagName) return [];

  const out: TagGranthaTarget[] = [];
  const seen = new Set<string>();

  const pushGranthaRef = (gRef: any) => {
    if (gRef?.id == null) return;
    if (!granthaReferenceHasShlokaTag(gRef, selectedTagName)) return;
    const key = getGranthaReferenceKey(Number(gRef.id));
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ key });
  };

  bidhiRoots.forEach((root) => walkBidhiGranthaReferences(root, pushGranthaRef));

  (puja?.lines || []).forEach((pl: any) => {
    (pl?.bidhiLinks || []).forEach((bl: any) => {
      if (bl?.bidhi) walkBidhiGranthaReferences(bl.bidhi, pushGranthaRef);
    });
  });

  return out;
}

export function collectTaggedStyleTargets(options: {
  selectedTagName: string;
  linesToRender: Array<{ id?: number; metadata?: unknown }>;
  bidhiRoots: any[];
  nestedAddedLines: Record<string, any[]>;
  puja?: { tags?: unknown; lines?: any[] } | null;
  entityWithTags?: unknown;
}): TaggedStyleTargets {
  const {
    selectedTagName,
    linesToRender,
    bidhiRoots,
    nestedAddedLines,
    puja,
    entityWithTags,
  } = options;

  const tagBlockTargets = collectTagBlockTargets(linesToRender, entityWithTags ?? puja);

  return {
    blockIds: collectTaggedBlockIds(
      tagBlockTargets,
      selectedTagName,
      linesToRender,
      entityWithTags ?? puja,
      bidhiRoots,
    ),
    nested: collectNestedTargetsForTag({ selectedTagName, bidhiRoots, nestedAddedLines, puja }),
    grantha: collectGranthaTargetsForTag({ selectedTagName, bidhiRoots, puja }),
  };
}
